import { Options } from 'material-table';
import React from 'react';
import type {
  Job,
  JobStatus as NumericJobStatus,
  JobStepStatus,
  JobType as NumericJobType,
  LocationSummary,
  UserIdentifier,
  UserSummary,
} from '../../app/ApiGen';
import { Address } from '../../components/Address';
import { JobStatusValue } from '../../components/CustomFields/JobStatus';
import type { ColumnDefinitions } from '../dataTable/columns';
import { getDisplayUser, locationHasAttributes, userHasAttributes } from '../format';
import type { Normalised } from '../request';
import { addressIncludesSearch, userIncludesSearch } from '../search';
import { sortByAddress } from '../sortBy';
import { JobStatusFilters } from './filterComponents/JobStatusFilters';
import { HumanReadableJobType, JobTypeFilters } from './filterComponents/JobTypeFilters';
import { filterBySelectableItem } from './filterComponents/SelectableFilters';
import { TextFieldFilter } from './filterComponents/TextFieldFilter';
import { YesNoNAOption } from './filterComponents/YesNoNAFilters';

export type JobListData = {
  address?: Normalised<LocationSummary>['address'];
  created: Date;
  customer: UserIdentifier | Normalised<UserSummary> | null;
  customerName: string;
  cocCompleted: YesNoNAOption;
  email?: string;
  icpNumber?: string;
  id: string;
  installer: UserIdentifier | Normalised<UserSummary> | null;
  installerLabel: string;
  organisationName: string;
  status: NumericJobStatus;
  type: NumericJobType;
  updated: Date;
  referenceId?: string;
};

type JobColumn = Exclude<keyof JobListData, 'created' | 'customer' | 'id' | 'installer' | 'updated' | 'cocCompleted'>;

export function jobListOptions<T extends JobListData>(): Options<T> {
  return {
    columnsButton: true,
    filtering: true,
    exportButton: true,
    exportFileName: 'jobs',
    loadingType: 'linear',
    pageSize: 50,
    search: true,
  };
}

export const jobColumns: ColumnDefinitions<JobListData, JobColumn> = {
  address: {
    field: 'address',
    title: 'Customer Address',
    hidden: true,
    render: (job) => (job.address ? <Address address={job.address} /> : ''),
    filterComponent: TextFieldFilter,
    customSort: sortByAddress('address'),
    customFilterAndSearch: (searchValue: unknown, { address }) =>
      address ? addressIncludesSearch(address, searchValue) : false,
    width: '35%',
  },
  customerName: {
    field: 'customerName',
    title: 'Customer Name',
    filterComponent: TextFieldFilter,
    customFilterAndSearch: (search: unknown, { customer, customerName }) =>
      userIncludesSearch({ user: customer, label: customerName, search }),
  },
  email: { field: 'email', title: 'Customer Email', filterComponent: TextFieldFilter, hidden: true, width: '15%' },
  icpNumber: {
    field: 'icpNumber',
    title: 'ICP/NMI number',
    filterComponent: TextFieldFilter,
    hidden: true,
    width: '10%',
  },
  installerLabel: {
    field: 'installerLabel',
    title: 'Installed By',
    hidden: true,
    filterComponent: TextFieldFilter,
    customFilterAndSearch: (search: unknown, { installer, installerLabel }) =>
      userIncludesSearch({ user: installer, label: installerLabel, search }),
    width: '15%',
  },
  organisationName: {
    field: 'organisationName',
    title: 'Organisation',
    filterComponent: TextFieldFilter,
  },
  referenceId: {
    field: 'referenceId',
    title: 'Reference ID',
    filterComponent: TextFieldFilter,
    filterCellStyle: { maxWidth: '6rem' },
  },
  status: {
    field: 'status',
    title: 'Status',
    type: 'numeric',
    render: (job) => <JobStatusValue value={job.status} />,
    filterCellStyle: { maxWidth: '6rem' },
    filterComponent: JobStatusFilters,
    customFilterAndSearch: (search: unknown, { status }) =>
      filterBySelectableItem<NumericJobStatus>(search, { value: status }),
  },
  type: {
    field: 'type',
    title: 'Job Type',
    type: 'numeric',
    render: ({ type }) => HumanReadableJobType[type],
    filterComponent: JobTypeFilters,
    customFilterAndSearch: (search: unknown, { type }) =>
      filterBySelectableItem<NumericJobType>(search, { value: type }),
  },
};

export function toJobListData(job: Normalised<Job>): JobListData {
  const location = locationHasAttributes(job.location) ? job.location : undefined;
  const progress = job.details.progress as
    | {
        certificateOfCompliance: JobStepStatus | undefined;
      }
    | undefined;
  let cocStatus: YesNoNAOption = 'N/A';
  if (progress) {
    cocStatus = progress.certificateOfCompliance === 'completed' ? 'Yes' : 'No';
  }

  return {
    id: job.id,
    address: location?.address,
    created: new Date(job.created),
    email: userHasAttributes(job.customer) ? job.customer.email : undefined,
    icpNumber: location?.icpNumber ?? job.details.icpNumber,
    installer: job.installer,
    installerLabel: getDisplayUser({ user: job.installer, noUserLabel: 'n/a' }),
    customer: job.customer,
    customerName: getDisplayUser({ user: job.customer, noUserLabel: '(unknown)' }),
    cocCompleted: cocStatus,
    organisationName: job.organisationName,
    status: job.status,
    type: job.jobType,
    updated: new Date(job.updated),
    referenceId: job.referenceId,
  };
}
